import type { StoredRecordType } from "../../../data/schemas";
import type { VersionedStorageService } from "../../../services/versioned-storage";
import type { UnresolvedTokenLogEntry } from "../normalization";
import type { PreflightIssue } from "./types";

export type PreflightFileRecordType =
  | "cue-positions"
  | "fills"
  | "lexicon-choices";

export interface LoadedPreflightRecords {
  readonly cuePositions: Readonly<Record<string, string>>;
  readonly fills: Readonly<Record<string, string>>;
  readonly issues: readonly PreflightIssue[];
  readonly lexiconChoices: Readonly<Record<string, string>>;
}

export function preflightStorageKey(
  recordType: StoredRecordType,
  identity: string,
): string {
  return `r1:${recordType}:${identity}`;
}

function loadFileValues(
  storage: VersionedStorageService,
  recordType: PreflightFileRecordType,
  fileHash: string,
): {
  readonly issue?: PreflightIssue;
  readonly values: Readonly<Record<string, string>>;
} {
  const key = preflightStorageKey(recordType, fileHash);
  const loaded = storage.load(key);
  if (!loaded.ok) {
    if (loaded.error.problem === "record not found") return { values: {} };
    return {
      values: {},
      issue: { source: loaded.error.source, message: loaded.error.message },
    };
  }
  if (loaded.value.recordType !== recordType) {
    return {
      values: {},
      issue: {
        source: `storage:${key}`,
        message: `storage:${key}: $.recordType: expected ${recordType}`,
      },
    };
  }
  const data = loaded.value.data;
  if (
    !("fileHash" in data) ||
    data.fileHash !== fileHash ||
    !("values" in data)
  ) {
    return {
      values: {},
      issue: {
        source: `storage:${key}`,
        message: `storage:${key}: $.data.fileHash: record does not match loaded file`,
      },
    };
  }
  return { values: data.values };
}

export function loadPreflightRecords(
  storage: VersionedStorageService,
  fileHash: string,
): LoadedPreflightRecords {
  const fills = loadFileValues(storage, "fills", fileHash);
  const cuePositions = loadFileValues(storage, "cue-positions", fileHash);
  const lexiconChoices = loadFileValues(storage, "lexicon-choices", fileHash);
  return {
    fills: fills.values,
    cuePositions: cuePositions.values,
    lexiconChoices: lexiconChoices.values,
    issues: [fills.issue, cuePositions.issue, lexiconChoices.issue].filter(
      (issue): issue is PreflightIssue => issue !== undefined,
    ),
  };
}

export function savePreflightValues(
  storage: VersionedStorageService,
  recordType: PreflightFileRecordType,
  fileHash: string,
  values: Readonly<Record<string, string>>,
): PreflightIssue | null {
  const key = preflightStorageKey(recordType, fileHash);
  const saved = storage.save(recordType, key, { fileHash, values });
  return saved.ok
    ? null
    : { source: saved.error.source, message: saved.error.message };
}

export function persistUnresolvedTokenLog(
  storage: VersionedStorageService,
  fileHash: string,
  entries: readonly UnresolvedTokenLogEntry[],
): readonly PreflightIssue[] {
  const issues: PreflightIssue[] = [];
  for (const entry of entries) {
    const key = preflightStorageKey("event", `${fileHash}:${entry.token}`);
    const saved = storage.save("event", key, {
      action: entry.action,
      message: entry.token,
      module: "orders-reader",
      stack: null,
    });
    if (!saved.ok) {
      issues.push({ source: saved.error.source, message: saved.error.message });
    }
  }
  return issues;
}
